import { defineStore } from 'pinia'
import casinoserve from '@/services/casinoserve.js'
import fix from '@/services/fix.js'
import { useSportStore } from '@/stores/sport.js'
import { useAuthStore } from '@/stores/auth.js'
import emitter from '@/services/eventbus.js'

export const useGameStore = defineStore('games', {
  state: () => ({
    rawGames: [],
    crashGames: [],
    casinoGames: [],
    instantGames: [],
    virtualsGames: [],
    quickWheelsGames: [],
    categories: [],
    favorites: [],
    providers: [],
    boosted: [],
    activeCategory: null,
    currentGameId: '',
    gamesFetched: false,
    categoriesFetched: false,
    limitedDataFetched: false,
    params: {
      sportId: useSportStore().getSportId,
      boosted: 1, today: 0, tomorrow: 0, upcoming: 0, per_page: 10
    },
    sportId: useSportStore().getSportId,
  }),
  actions: {
    async initializeGames() {
      await this.fetchAllData();
      await this.fetchFavorites();
      this.mergeGamesWithCategories();
    },
    async initializeLimitedGames() {
      if (!this.limitedDataFetched) {
        await this.fetchLimitedData();
      }
      this.mergeGamesWithCategories();
    },

    async fetchBoostedGames(){
      const url = `/highlights/${this.sportId}`;
      try {
        const response = await fix.get(url, { params: this.params });
        this.boosted = response.data.data;
      } catch (error) {
        console.error('Failed to fetch sports data:', error);
      }
    },

    async fetchProviders() {
      this.loading = true;
      try {
        const response = await casinoserve.get(`/providers`);
        if (response.status === 200) {
          this.providers = response.data;
        } else {
          console.error('Failed to fetch providers:', response.status);
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchFavorites() {
      const authStore = useAuthStore();
      if (!authStore.user) return;

      const authToken = authStore.user?.auth;
      try {
        const response = await casinoserve.get(
          `/favourite`,
          {
            params: { page: 1, per_page: 50, sort: 'games.priority|desc' },
            headers: { 'api-key': authToken },
          }
        );

        if (response.status === 200) {
          this.favorites = response.data.data;
        } else {
          console.error('Failed to fetch favorites:', response);
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
      }
    },

    async isFavorite(gid) {
      // console.log("Is Favorite?", this.favorites.some(favorite => favorite.id === gid))
      return this.favorites.some(favorite => favorite.id === gid);
    },

    async addFavorite(gid) {
      const authStore = useAuthStore();
      if (!authStore.user) return;

      const authToken = authStore.user?.auth;
      try {
        const response = await casinoserve.post(
          `/favourite/${gid}`,
          { id: parseInt(gid) },
          {
            headers: { 'api-key': authToken },
          }
        );

        if (response.status === 200 || response.status === 201) {
          this.favorites.push({ id: gid });

          localStorage.setItem('favorites', JSON.stringify(this.favorites));
        } else {
          console.error('Failed to add favorite on server:', response);
        }
      } catch (error) {
        console.error('Error adding favorite to the server:', error);
      }
    },

    async removeFavorite(gid) {
      const authStore = useAuthStore();
      if (!authStore.user) return;

      const authToken = authStore.user?.auth;
      try {
        const response = await casinoserve.delete(
          `/favourite/${gid}`,
          {
            headers: { 'api-key': authToken },
          }
        );

        if (response.status === 200 || response.status === 201) {
          this.favorites = this.favorites.filter(fav => fav.id !== gid);
          localStorage.setItem('favorites', JSON.stringify(this.favorites));
        }
      } catch (error) {
        console.error('Error removing favorite:', error);
      }
    },

    async fetchAllData() {
      if (this.gamesFetched && this.categoriesFetched) {
        return;
      }

      try {
        const gamesPath = `/games`;
        const categoriesPath = `/category`;

        const gamesResponse = await casinoserve.get(gamesPath, { params: { page: 1, per_page: 2000 } });

        if (gamesResponse.status === 200) {
          this.rawGames = gamesResponse.data.data;
          this.crashGames = this.processGames(this.rawGames, 'Crashpop');
          this.casinoGames = this.processGames(this.rawGames, 'Casinopop');
          this.instantGames = this.processGames(this.rawGames, 'Instantpop');
          this.virtualsGames = this.processGames(this.rawGames, 'Virtualspop');
          this.quickWheelsGames = this.processGames(this.rawGames, 'Quick Wheelspop');
          this.gamesFetched = true;
        } else {
          console.error('Failed to get games. Status:', gamesResponse.status);
        }

        const categoriesResponse = await casinoserve.get(categoriesPath);

        if (categoriesResponse.status === 200) {
          this.categories = categoriesResponse.data.map((category) => ({
            id: category.id,
            name: category.name,
            games: [],
            priority: category.priority || 0,
          }));
          this.categories.sort((a, b) => b.priority - a.priority);
          this.categoriesFetched = true;
        } else {
          console.error('Failed to get categories. Status:', categoriesResponse.status);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    },

    async fetchLimitedData() {
      try {
        const gamesPath = `/games`;
        const categoriesPath = `/category`;

        const gamesResponse = await casinoserve.get(gamesPath, { params: { page: 1, per_page: 100 } });

        if (gamesResponse.status === 200) {
          this.rawGames = gamesResponse.data.data;
          this.crashGames = this.processGames(this.rawGames, 'Crashpop');
          this.casinoGames = this.processGames(this.rawGames, 'Casinopop');
          this.instantGames = this.processGames(this.rawGames, 'Instantpop');
          this.virtualsGames = this.processGames(this.rawGames, 'Virtualspop');
          this.quickWheelsGames = this.processGames(this.rawGames, 'Quick Wheelspop');
          this.limitedDataFetched = true;
        } else {
          console.error('Failed to get games. Status:', gamesResponse.status);
        }

        if (!this.categoriesFetched) {
          const categoriesResponse = await casinoserve.get(categoriesPath);

          if (categoriesResponse.status === 200) {
            this.categories = categoriesResponse.data.map((category) => ({
              id: category.id,
              name: category.name,
              games: [],
              priority: category.priority || 0,
            }));
            this.categories.sort((a, b) => b.priority - a.priority);
            this.categoriesFetched = true;
          } else {
            console.error('Failed to get categories. Status:', categoriesResponse.status);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    },
    processGames(allGames, category) {
      return allGames
        .filter((game) => game.category.includes(category))
        .map((gameData) => {
          const categories = gameData.category.split(',');
          let launchType = 1;
          if (categories.includes('Live games')) {
            launchType = 2;
          }

          const gameName = gameData.game_name.replace('kiron', 'Ligi Soo');
          const capitalizedGameName = gameName.charAt(0).toUpperCase() + gameName.slice(1);

          return {
            id: gameData.game_id,
            category: gameData.category,
            name: capitalizedGameName,
            image: this.adjustImageURL(gameData.image),
            providerId: gameData.provider_id,
            launchType: launchType,
          };
        })
        .slice(0, 7);
    },

    adjustImageURL(imageURL) {
      const isValidURL = (url) => {
        return url && url.startsWith('https://') && (url.endsWith('.jpg') || url.endsWith('.png') || url.endsWith('.webp') || url.endsWith('.jpeg'));
      };

      if (isValidURL(imageURL)) {
        return imageURL
          .replace('https://storage.googleapis.com/maybet/smartsoft/', 'https://smartsoft.maybets.com/assets/')
          .replace('https://smartsoft.maybets.com/assets/JetX.png', 'https://storage.googleapis.com/maybet/site/casino_assets/jetx.png')
          .replace('https://storage.googleapis.com/maybet/smartsoft/', 'https://smartsoft.maybets.com/assets/')
          // .replace('https://storage.googleapis.com/maybet/spribe/', 'https://spribe.maybets.com/assets/')
          .replace('https://storage.googleapis.com/maybet/elbet/', 'https://elbet.maybets.com/assets/')
          .replace('https://storage.googleapis.com/maybet/smartsoft/', 'https://smartsoft.maybets.com/assets/')
          .replace('https://storage.googleapis.com/maybet/spribe/', 'https://spribe.maybets.com/assets/')
          .replace('https://storage.googleapis.com/maybet/elbet/', 'https://elbet.maybets.com/assets/')
          .replace('https://spribe.maybets.com/assets/aviator.png', 'https://storage.googleapis.com/maybet/images/aviator.png')
          // .replace('https://elbet.maybets.com/assets/rocketman.png', 'https://storage.googleapis.com/maybet/images/rocketman.png')
          // .replace('https://cdn.xpress-ix.com/images/_2306320.png', 'https://storage.googleapis.com/maybet/site/assets/icons/NUMBERGAMES-new.jpg')

          .replace('https://storage.googleapis.com/maybet/imoon/1006.png', 'https://storage.googleapis.com/maybet/site/casino_assets/crash1917.png')
          .replace('https://storage.googleapis.com/maybet/imoon/1001.png', 'https://storage.googleapis.com/maybet/site/casino_assets/crashroyale.png')



        // .replace('https://api-dk11.pragmaticplay.net/game_pic/square/200/vs15diamond.png', 'https://storage.googleapis.com/maybet/images/vs15diamond.png')
        // .replace('https://api-dk11.pragmaticplay.net/game_pic/square/200/302.png', 'https://storage.googleapis.com/maybet/images/302.png')
        // .replace('https://cdn.xpressgaming.net/images/47_1006699.png', 'https://storage.googleapis.com/maybet/site/assets/icons/new-VIRTUAL-SPORTS-ICONSENGLAND-ON-DEMAND%20(1)%20(1).png')
        // .replace('https://cdn.xpressgaming.net/images/47_9481749.png', 'https://storage.googleapis.com/maybet/images/47_9481749.png')
        // .replace('https://cdn.xpressgaming.net/images/47_6627174.png', 'https://storage.googleapis.com/maybet/site/assets/icons/new-VIRTUAL-SPORTS-ICONSENGLAND%20(1).png')
        // .replace('https://cdn.xpressgaming.net/images/47_183402.png', 'https://storage.googleapis.com/maybet/site/assets/icons/new-VIRTUAL-SPORTS-ICONSSPAIN%20(1)%20(1).png')
        // .replace('https://cdn.xpressgaming.net/images/47_3947312.png', 'https://storage.googleapis.com/maybet/site/assets/icons/new-VIRTUAL-SPORTS-ICONSEURO%20(1)%20(1).png')
        // .replace('https://cdn.xpressgaming.net/images/47_4694846.png', 'https://storage.googleapis.com/maybet/images/47_4694846.png')
        // .replace('https://cdn.xpressgaming.net/images/47_9868579.png', 'https://storage.googleapis.com/maybet/site/assets/icons/spin2win-new.webp')
        // .replace('https://cdn.xpressgaming.net/images/47_9313552.png', 'https://storage.googleapis.com/maybet/site/assets/images/a-italyvirtualleague.png')
        // .replace('https://cdn.xpressgaming.net/images/47_7871526.png', 'https://storage.googleapis.com/maybet/site/assets/images/new-VIRTUAL-SPORTS-ICONSSPAIN-ON-DEMAND.png')
        // .replace('https://cdn.xpressgaming.net/images/47_1662251.png', 'https://storage.googleapis.com/maybet/site/assets/icons/DOGRACE-new2.png')
        // .replace('https://cdn.xpressgaming.net/images/47_2416037.png', 'https://storage.googleapis.com/maybet/site/assets/icons/keno80-new.webp')
        // .replace('https://cdn.xpressgaming.net/images/47_6421164.png', 'https://storage.googleapis.com/maybet/site/assets/icons/DOGRACE6-new.png')
        // .replace('https://cdn.xpress-ix.com/images/31_7347931.png', 'https://storage.googleapis.com/maybet/site/assets/icons/dontdroptheball-new.webp')
        // .replace('https://cdn.xpress-ix.com/images/30_2932315.png', 'https://storage.googleapis.com/maybet/site/assets/icons/finalfist-new.webp')
        // .replace('https://cdn.xpressgaming.net/images/47_3642540.png', 'https://storage.googleapis.com/maybet/site/assets/images/a-uefavirtualleague.webp')
        // .replace('https://cdn.xpressgaming.net/images/47_8323692.png', 'https://storage.googleapis.com/maybet/site/assets/icons/GOLDENRACE2-new.webp')
        // .replace('https://cdn.xpressgaming.net/images/47_8244677.png', 'https://storage.googleapis.com/maybet/site/assets/icons/HORSE6-new.png')
        // .replace('https://cdn.xpressgaming.net/images/47_2091191.png', 'https://storage.googleapis.com/maybet/site/assets/icons/instantsports2-new.webp');
      } else {
        return '/assets/images/backup/casinholder.png';
      }
    },

    setActiveCategory(category) {
      this.activeCategory = category;
    },
    toggleOverlay(game) {
      this.currentGameId = game.providerId + '.' + game.id;
    },
    getOverlayClass(game) {
      return this.currentGameId === game.providerId + '.' + game.id ? 'casino-overlay-visible' : '';
    },
    mergeGamesWithCategories() {
      this.categories = this.categories.map((category) => ({
        ...category,
        games: this.rawGames
          .filter((game) => game.category.includes(category.name))
          .map((gameData) => ({
            id: gameData.game_id,
            gid: gameData.id,
            name: gameData.game_name
              .replace('kiron', 'Ligi Soo'),
            image: this.adjustImageURL(gameData.image),
            providerId: gameData.provider_id,
            launchType: gameData.category.includes('Live games') ? 2 : 1,
          }))
      }));
    }
  }
});